// Ember web API client — ported from /app/mobile/src/api.ts.
// Shared backend contract; browser localStorage + fetch/Blob instead of RN storage.
import { logError } from "@/lib/log";

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api`;

const USER_KEY = "cozyvoice_user_id";
const TOKEN_KEY = "cozyvoice_session_token";
const FAMILY_ACCOUNT_KEY = "cozyvoice_family_account_token";

export const PRIVACY_URL = `${BASE}/api/privacy`;

/* --------------------------- storage helpers --------------------------- */
export function getStoredUserId() {
  return localStorage.getItem(USER_KEY);
}
export function setStoredUserId(id) {
  localStorage.setItem(USER_KEY, id);
}
export function getSessionToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function setSessionToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}
export function clearUser() {
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(TOKEN_KEY);
}

function authHeaders(tokenOverride) {
  const t = tokenOverride || getSessionToken();
  return t ? { "X-Session-Token": t } : {};
}

/* ------------------------------ core user ------------------------------ */
export async function createUser(payload) {
  const res = await fetch(`${API}/users`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error("Failed to create profile");
  const data = await res.json();
  if (data.session_token) setSessionToken(data.session_token);
  return data;
}

export async function fetchUser(userId) {
  const res = await fetch(`${API}/users/${userId}`, { headers: authHeaders() });
  if (!res.ok) throw new Error("Failed to load profile");
  return res.json();
}

export async function deleteUserData(userId) {
  try {
    await fetch(`${API}/users/${userId}`, { method: "DELETE", headers: authHeaders() });
  } catch (e) { logError("deleteUserData", e); }
  clearUser();
}

/* --------------------------------- chat -------------------------------- */
export async function sendText(userId, text) {
  const res = await fetch(`${API}/chat/text`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ user_id: userId, text, want_audio: true }),
  });
  if (!res.ok) throw new Error("Failed to get a reply");
  return res.json();
}

export async function sendVoice(userId, blob) {
  const form = new FormData();
  form.append("user_id", userId);
  form.append("audio", blob, "speech.webm");
  const res = await fetch(`${API}/chat/voice`, { method: "POST", body: form, headers: authHeaders() });
  if (!res.ok) throw new Error("Could not understand the audio");
  return res.json();
}

export async function fetchTts(text) {
  try {
    const res = await fetch(`${API}/tts`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ text }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.audio ?? null;
  } catch {
    return null;
  }
}

export async function fetchOpener(userId) {
  try {
    const res = await fetch(`${API}/opener/${userId}`, { headers: authHeaders() });
    if (!res.ok) return null;
    return (await res.json()).message ?? null;
  } catch {
    return null;
  }
}

export async function fetchCheckin(userId) {
  try {
    const res = await fetch(`${API}/checkin/${userId}`, { headers: authHeaders() });
    if (!res.ok) return null;
    return (await res.json()).message ?? null;
  } catch {
    return null;
  }
}

export async function fetchHistory(userId) {
  try {
    const res = await fetch(`${API}/history/${userId}`, { headers: authHeaders() });
    if (!res.ok) return [];
    return (await res.json()).messages ?? [];
  } catch {
    return [];
  }
}

export async function fetchNudges(userId) {
  try {
    const res = await fetch(`${API}/nudges/${userId}`, { headers: authHeaders() });
    if (!res.ok) return [];
    return (await res.json()).lines ?? [];
  } catch {
    return [];
  }
}

export async function fetchStats(userId) {
  try {
    const res = await fetch(`${API}/stats/${userId}`, { headers: authHeaders() });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

export async function seedDemo(userId) {
  try {
    const res = await fetch(`${API}/users/${userId}/seed-demo`, { method: "POST", headers: authHeaders() });
    return res.ok;
  } catch {
    return false;
  }
}

export async function resetDemo(userId) {
  try {
    const res = await fetch(`${API}/users/${userId}/reset-demo`, { method: "POST", headers: authHeaders() });
    return res.ok;
  } catch {
    return false;
  }
}

export async function fetchSummaries(userId) {
  try {
    const res = await fetch(`${API}/summaries/${userId}`, { headers: authHeaders() });
    if (!res.ok) return [];
    return (await res.json()).summaries ?? [];
  } catch {
    return [];
  }
}

export async function summarizeCall(userId) {
  try {
    await fetch(`${API}/conversations/${userId}/summarize`, { method: "POST", headers: authHeaders() });
  } catch (e) { logError("summarizeCall", e); }
}

/* ---------------------------- voice settings --------------------------- */
export async function fetchVoiceSettings() {
  try {
    const res = await fetch(`${API}/voice-settings`);
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

export async function saveVoiceSettings(s) {
  try {
    const res = await fetch(`${API}/voice-settings`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(s),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/* -------------------------- notification prefs ------------------------- */
export async function fetchNotificationPrefs(userId, token) {
  try {
    const res = await fetch(`${API}/users/${userId}/notifications`, { headers: authHeaders(token) });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

export async function setShareSummaries(userId, enabled, token) {
  try {
    const res = await fetch(`${API}/users/${userId}/share-summaries`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders(token) },
      body: JSON.stringify({ enabled }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function setFamilyEmail(userId, email, token) {
  try {
    const res = await fetch(`${API}/users/${userId}/family-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders(token) },
      body: JSON.stringify({ email }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function sendTestEmail(userId, token) {
  try {
    const res = await fetch(`${API}/users/${userId}/test-email`, { method: "POST", headers: authHeaders(token) });
    if (!res.ok) return { ok: false, reason: "request_failed" };
    return res.json();
  } catch {
    return { ok: false, reason: "request_failed" };
  }
}

export async function setFamilyPhone(userId, phone, token) {
  try {
    const res = await fetch(`${API}/users/${userId}/family-phone`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders(token) },
      body: JSON.stringify({ phone }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      return { ok: false, error: data.detail || "Could not save the phone number." };
    }
    return { ok: true };
  } catch {
    return { ok: false, error: "Network error. Please try again." };
  }
}

export async function setSmsSummaries(userId, enabled, token) {
  try {
    const res = await fetch(`${API}/users/${userId}/sms-summaries`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders(token) },
      body: JSON.stringify({ enabled }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function sendTestSms(userId, token) {
  try {
    const res = await fetch(`${API}/users/${userId}/test-sms`, { method: "POST", headers: authHeaders(token) });
    if (!res.ok) return { ok: false, reason: "request_failed" };
    return res.json();
  } catch {
    return { ok: false, reason: "request_failed" };
  }
}

/* ----------------------------- family code ----------------------------- */
export async function generateFamilyCode(userId) {
  try {
    const res = await fetch(`${API}/users/${userId}/family-code`, { method: "POST", headers: authHeaders() });
    if (!res.ok) return null;
    return (await res.json()).code ?? null;
  } catch {
    return null;
  }
}

export async function regenerateFamilyCode(userId) {
  try {
    const res = await fetch(`${API}/users/${userId}/family-code`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ regenerate: true }),
    });
    if (!res.ok) return null;
    return (await res.json()).code ?? null;
  } catch {
    return null;
  }
}

export async function verifyFamilyCode(code) {
  try {
    const res = await fetch(`${API}/family/verify?code=${encodeURIComponent(code)}`);
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

/* ------------------------------- personas ------------------------------ */
export async function listPersonas(userId, token) {
  try {
    const res = await fetch(`${API}/users/${userId}/personas`, { headers: authHeaders(token) });
    if (!res.ok) return { personas: [], elevenlabs_enabled: false };
    return res.json();
  } catch {
    return { personas: [], elevenlabs_enabled: false };
  }
}

export async function createPersona(userId, payload, token) {
  try {
    const form = new FormData();
    form.append("name", payload.name);
    form.append("relationship", payload.relationship);
    form.append("status", payload.status);
    form.append("attested_by", payload.attested_by);
    form.append("has_consent", payload.has_consent ? "true" : "false");
    form.append("is_authorized", payload.is_authorized ? "true" : "false");
    form.append("consent_signature", payload.consent_signature || "");
    form.append("speaking_style", payload.speaking_style || "");
    form.append("comfort_words", payload.comfort_words || "");
    form.append("topics_lean_in", payload.topics_lean_in || "");
    form.append("topics_avoid", payload.topics_avoid || "");
    form.append("mode_locked", payload.mode_locked ? "true" : "false");
    form.append("audio", payload.audio.blob, payload.audio.name || "voice-sample.webm");
    const res = await fetch(`${API}/users/${userId}/personas`, {
      method: "POST",
      body: form,
      headers: authHeaders(token),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: data?.detail || "Something went wrong. Please try again." };
    return { ok: true, persona: data.persona, quality_warning: data.quality_warning };
  } catch {
    return { ok: false, error: "Upload failed. Please check your connection and try again." };
  }
}

export async function activatePersona(id, token) {
  try {
    const res = await fetch(`${API}/personas/${id}/activate`, { method: "POST", headers: authHeaders(token) });
    return res.ok;
  } catch {
    return false;
  }
}

export async function deactivatePersona(id, token) {
  try {
    const res = await fetch(`${API}/personas/${id}/deactivate`, { method: "POST", headers: authHeaders(token) });
    return res.ok;
  } catch {
    return false;
  }
}

export async function deletePersona(id, token) {
  try {
    const res = await fetch(`${API}/personas/${id}`, { method: "DELETE", headers: authHeaders(token) });
    return res.ok;
  } catch {
    return false;
  }
}

export async function previewPersona(id, token) {
  try {
    const res = await fetch(`${API}/personas/${id}/preview`, { headers: authHeaders(token) });
    if (!res.ok) return null;
    return (await res.json()).audio ?? null;
  } catch {
    return null;
  }
}

export async function setPersonaLock(id, locked, token) {
  try {
    const res = await fetch(`${API}/personas/${id}/mode-lock`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders(token) },
      body: JSON.stringify({ locked }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function setCompanionMode(userId, familiar) {
  try {
    const res = await fetch(`${API}/users/${userId}/companion-mode`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ familiar }),
    });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

/* ----------------------- family member accounts ------------------------ */
export function getFamilyAccountToken() {
  return localStorage.getItem(FAMILY_ACCOUNT_KEY);
}
function setFamilyAccountToken(token) {
  localStorage.setItem(FAMILY_ACCOUNT_KEY, token);
}
export function clearFamilyAccountToken() {
  localStorage.removeItem(FAMILY_ACCOUNT_KEY);
}
function accountHeaders() {
  const t = getFamilyAccountToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export async function familyAccountRegister(email, password, displayName) {
  try {
    const res = await fetch(`${API}/family-auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, display_name: displayName }),
    });
    const data = await res.json();
    if (!res.ok) return { ok: false, error: data.detail || "Could not create account." };
    setFamilyAccountToken(data.token);
    return { ok: true, account: data.account };
  } catch {
    return { ok: false, error: "Network error. Please try again." };
  }
}

export async function familyAccountGoogle(sessionId) {
  try {
    const res = await fetch(`${API}/family-auth/google-session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sessionId }),
    });
    const data = await res.json();
    if (!res.ok) return { ok: false, error: data.detail || "Google sign-in failed." };
    setFamilyAccountToken(data.token);
    return { ok: true, account: data.account };
  } catch {
    return { ok: false, error: "Network error. Please try again." };
  }
}

export async function familyAccountLogin(email, password) {
  try {
    const res = await fetch(`${API}/family-auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) return { ok: false, error: data.detail || "Could not sign in." };
    setFamilyAccountToken(data.token);
    return { ok: true, account: data.account };
  } catch {
    return { ok: false, error: "Network error. Please try again." };
  }
}

export async function familyAccountMe() {
  try {
    const res = await fetch(`${API}/family-auth/me`, { headers: accountHeaders() });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

export async function familyAccountLink(code) {
  try {
    const res = await fetch(`${API}/family-auth/link`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...accountHeaders() },
      body: JSON.stringify({ code }),
    });
    const data = await res.json();
    if (!res.ok) return { ok: false, error: data.detail || "Could not link that code." };
    return { ok: true, lovedOne: { user_id: data.user_id, first_name: data.first_name } };
  } catch {
    return { ok: false, error: "Network error. Please try again." };
  }
}

export async function familyAccountOpenSession(userId) {
  try {
    const res = await fetch(`${API}/family-auth/loved-ones/${userId}/session`, {
      method: "POST",
      headers: accountHeaders(),
    });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

export async function familyAccountLogout() {
  try {
    await fetch(`${API}/family-auth/logout`, { method: "POST", headers: accountHeaders() });
  } catch (e) { logError("familyAccountLogout", e); }
  clearFamilyAccountToken();
}

export async function familyAccountDelete() {
  let ok = false;
  try {
    const res = await fetch(`${API}/family-auth/account`, { method: "DELETE", headers: accountHeaders() });
    ok = res.ok;
  } catch {
    ok = false;
  }
  clearFamilyAccountToken();
  return ok;
}

/* ---------------------------- premium (PayPal) ------------------------- */
export async function paymentStatus() {
  try {
    const res = await fetch(`${API}/payments/status`, { headers: accountHeaders() });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

export async function createPaypalOrder(returnUrl, cancelUrl) {
  try {
    const res = await fetch(`${API}/payments/create-order`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...accountHeaders() },
      body: JSON.stringify({ return_url: returnUrl, cancel_url: cancelUrl }),
    });
    if (!res.ok) return { ok: false, reason: "request_failed" };
    return res.json();
  } catch {
    return { ok: false, reason: "request_failed" };
  }
}

export async function capturePaypalOrder(orderId) {
  try {
    const res = await fetch(`${API}/payments/capture-order`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...accountHeaders() },
      body: JSON.stringify({ order_id: orderId }),
    });
    if (!res.ok) return { ok: false, reason: "request_failed" };
    return res.json();
  } catch {
    return { ok: false, reason: "request_failed" };
  }
}

/* -------------------------------- moods -------------------------------- */
export const MOODS = [
  { key: "gentle", label: "Gentle", icon: "feather", values: { stability: 0.75, similarity_boost: 0.8, style: 0.15 } },
  { key: "cheerful", label: "Cheerful", icon: "sun", values: { stability: 0.35, similarity_boost: 0.75, style: 0.55 } },
  { key: "calm", label: "Calm", icon: "moon", values: { stability: 0.9, similarity_boost: 0.8, style: 0.05 } },
];

export function matchMood(v) {
  if (!v) return null;
  return (
    MOODS.find(
      (m) =>
        Math.abs(v.stability - m.values.stability) < 0.02 &&
        Math.abs(v.similarity_boost - m.values.similarity_boost) < 0.02 &&
        Math.abs(v.style - m.values.style) < 0.02
    ) ?? null
  );
}
